const sortBy = require('lodash/sortBy');
const startCase = require('lodash/startCase');
const moment = require('moment');
const fromPairs = require('lodash/fromPairs');
const sumBy = require('lodash/sumBy');
const { unitLabel } = require('./labels');
const routes = require('../config/routes');
const adminTypes = require('../config/adminTypes');
const { interventions } = require('../config/interventions');
const iv = require('../config/iv');
const physicalAssessmentLocations = require('../config/physicalAssessmentLocations');

const actionTypeOrderMap = {
  QUESTION: 0,
  MEDICATION: 1,
  MEDICATION_ROUTE: 2,
  IV_SITE: 3,
  IV_GAUGE: 4,
  IV_SET: 5,
  IV_FLUID: 6,
  IV_FLUID_RATE: 7,
  IV_VOLUME: 8,
  IV_TUBING: 9,
  IV_SALINE_LOCK: 10,
  OXYGEN_ROUTE: 11,
  PROCEDURE: 12,
  LEAD_SELECT: 13,
  SYNC: 14,
  DEFIBRILLATION: 15,
  PACING: 16,
};

const allActions = (report) => {
  return sortBy(
    [
      ...report.successfullyTakenActions.map((a) => ({
        ...a,
        grade: 'NEED',
        successful: true,
      })),
      ...report.successfullyAvoidedActions.map((a) => ({
        ...a,
        grade: 'AVOID',
        successful: true,
      })),
      ...report.failedToTakeActions.map((a) => ({
        ...a,
        grade: 'NEED',
        successful: false,
      })),
      ...report.failedToAvoidActions.map((a) => ({
        ...a,
        grade: 'AVOID',
        successful: false,
      })),
    ],
    (action) => actionTypeOrderMap[action.type]
  );
};

const totalPoints = (report) => {
  return [
    ...report.successfullyTakenActions,
    ...report.successfullyAvoidedActions,
    ...report.failedToTakeActions,
    ...report.failedToAvoidActions,
  ].reduce((sum, action) => sum + action.points, 0);
};

const successfulPoints = (report) => {
  return [
    ...report.successfullyTakenActions,
    ...report.successfullyAvoidedActions,
  ].reduce((sum, action) => sum + action.points, 0);
};

const percentage = (report) => {
  return Math.round((report.grade / totalPoints(report)) * 100);
};

const typeLabels = {
  QUESTION: 'Question',
  MEDICATION: 'Medication',
  MEDICATION_ROUTE: 'Medication',
  OXYGEN: 'Oxygen',
  PROCEDURE: 'Procedure',
  PACING: 'Pacing',
  LAB_ASSET_ORDER: 'Lab Asset',
  LAB_ASSET_VIEW: 'Lab Asset',
  LAB_RESULT_ORDER: 'Lab Result',
  LAB_RESULT_VIEW: 'Lab Result',
  COMPRESSION: 'Compressions',
  IV_ACTION: 'IV',
  VITALS: 'Vitals',
  LEADS: 'Leads',
  TRANSFER: 'Transfer',
  PERSONAL_PROTECTIVE_EQUIPMENT: 'PPE',
  VENTILATOR: 'Ventilator',
  PALPABLE_PULSE: 'Pulse Taken',
  GRADING: 'Case Logic',
  ASSESSMENT: 'Assessment',
  IV_SITE: 'IV Site',
};

const typeVerbs = {
  QUESTION: 'Asked',
  MEDICATION: 'Administered',
  MEDICATION_ROUTE: 'Administered',
  OXYGEN: 'Given',
  PROCEDURE: 'Done',
  PACING: 'Done',
  LAB_ASSET_ORDER: 'Ordered',
  LAB_ASSET_VIEW: 'Viewed',
  LAB_RESULT_ORDER: 'Ordered',
  LAB_RESULT_VIEW: 'Viewed',
  COMPRESSION: 'Administered',
  IV_ACTION: 'Administered ',
  VITALS: 'Assessed',
  LEADS: 'Attached',
  TRANSFER: 'Done',
  PERSONAL_PROTECTIVE_EQUIPMENT: 'Donned',
  VENTILATOR: 'Administered',
  PALPABLE_PULSE: 'Taken',
};

function actionLabel(action) {
  if (action.type === 'OXYGEN' || action.type === 'OXYGEN_ROUTE' || action.type === 'OXYGEN_TYPE') {
    return {
      CPAP: 'CPAP',
      BVM: 'BVM',
      NASAL_CANNULA: 'Nasal Cannula',
      NON_REBREATHER: 'Non Rebreather',
      SIMPLE_FACE_MASK: 'Simple Face Mask',
      INTUBATION: 'Intubation',
    }[action.typeID];
  }
  if (action.type === 'ASSESSMENT') {
    return {
      PENLIGHT: 'Penlight',
      HEART_SOUNDS: 'Heart Sounds',
      LUNG_SOUNDS: 'Lung Sounds',
      PHYSICAL: 'Physical Assessment',
      STETHOSCOPE_START: 'Start Stethoscope',
    }[action.typeID];
  }
  if (action.type === 'PROCEDURE') {
    return {
      VAGAL_MANEUVER: 'Vagal Maneuver',
      RAISE_BED_RAIL: 'Raised Bedrail',
      LOWER_BED_RAIL: 'Lower Bedrail',
      NASAL_SWAB: 'Nasal Swab',
      UPPER_CLOTHES_REMOVED: 'Remove Upper Clothes',
      LOWER_CLOTHES_REMOVED: 'Remove Lower Clothes',
      UPPER_UNDERWEAR_REMOVED: 'Remove Upper Underwear',
      LOWER_UNDERWEAR_REMOVED: 'Remove Lower Underwear',
    }[action.typeID];
  }
  if (action.type === 'PACING') {
    return {
      DEFIBRILLATION: 'Defibrillation',
      SYNC: 'Sync',
      TRANSCUTANEOUS: 'Transcutaneous pacing',
    }[action.typeID];
  }
  if (action.type === 'LAB_ASSET_ORDER') {
    return {
      LEADS: 'Order leads',
      BLOOD: 'Order blood',
      ORDERS: 'Order orders',
      IMAGES: 'Order images',
    }[action.typeID];
  }
  if (action.type === 'LAB_ASSET_VIEW') {
    return {
      LEADS: 'View leads',
      BLOOD: 'View blood',
      ORDERS: 'View orders',
      IMAGES: 'View images',
    }[action.typeID];
  }
  if (action.type === 'LAB_RESULT_ORDER') {
    return {
      GLUCOSE: 'Order glucose',
    }[action.typeID];
  }
  if (action.type === 'LAB_RESULT_VIEW') {
    return {
      GLUCOSE: 'View glucose',
    }[action.typeID];
  }
  if (action.type === 'COMPRESSION') {
    return {
      CPR: 'CPR',
    }[action.typeID];
  }
  if (action.type === 'VITALS') {
    return {
      BLOOD_PRESSURE_BOTH: 'BP',
      BLOOD_PRESSURE_SYSTOLIC: 'BP Systolic',
      BLOOD_PRESSURE_DIASTOLIC: 'BP Diastolic',
      // Because grading reports are immutable by nature
      // we still map the PULSE vital to the Heart Rate label
      PULSE: 'Heart Rate',
      HEART_RATE: 'Heart Rate',
      RESPIRATORY_RATE: 'Respiratory Rate',
      SPO2: 'SPO2',
      ETCO2: 'ETCO2',
      TEMPERATURE: 'Temperature',
      HEART_RHYTHM: 'Heart Rhythm',
      ETCO2_RHYTHM: 'ETCO2 Rhythm',
    }[action.typeID];
  }
  if (action.type === 'LEAD_SELECT' || action.type === 'LEADS') {
    return {
      LEAD_I: 'Lead I',
      LEAD_II: 'Lead II',
      LEAD_III: 'Lead III',
      PADS: 'Pads',
      TWELVE_LEADS: '12 Leads',
    }[action.typeID];
  }
  if (action.type === 'TRANSFER') {
    return 'Transfer';
  }
  if (action.type === 'PERSONAL_PROTECTIVE_EQUIPMENT') {
    return {
      DOUBLE_GLOVES: 'Double Gloves',
      FACE_MASK: 'Face Mask',
      FACE_SHIELD: 'Face Shield',
      GLOVES: 'Gloves',
      GOGGLES: 'Goggles',
      GOWN: 'Gown',
      N95: 'N95',
      PAPR: 'PAPR',
      PATIENT_FACE_MASK: 'Patient Face Mask',
      TYVEK_SUIT: 'Tyvek Suit',
      PERSONAL_PROTECTIVE_EQUIPMENT: 'PPE',
    }[action.typeID];
  }
  if (action.type === 'IV_SALINE_LOCK') {
    return 'Saline Lock';
  }
  if (action.type === 'IV_SITE') {
    const prefix = action.subComponentKey ?
      {
        LEFT: 'Left ',
        RIGHT: 'Right ',
        BOTH: 'Center '
      }[action.subComponentKey]
      : '';
      const site = iv.sites.find(site => site.key === action.typeID);
      const suffix = site ? site.label.en : 'UNKNOWN';
      return prefix + suffix;
  }
  if (action.type === 'IV_GAUGE') {
    return {
      GAUGE_14: '14G',
      GAUGE_16: '16G',
      GAUGE_18: '18G',
      GAUGE_20: '20G',
      GAUGE_22: '22G',
      NEEDLE_SET_15: '15mm',
      NEEDLE_SET_25: '25mm',
      NEEDLE_SET_45: '45mm',
    }[action.typeID];
  }
  if (action.type === 'IV_FLUID') {
    return {
      NORMAL_SALINE: 'Normal Saline',
      D5W: 'D5W',
      LACTATED_RINGERS: 'Lactated Ringers',
    }[action.typeID];
  }
  if (action.type === 'IV_TUBING') {
    return {
      GTT_10: '10 GTT',
      GTT_15: '15 GTT',
      GTT_60: '60 GTT',
    }[action.typeID];
  }
  if (action.type === 'IV_RATE') {
    return {
      CUSTOM: 'Custom',
      BOLUS: 'Bolus',
      KVO_TKO: 'KVO/TKO',
    }[action.typeID];
  }
  if (action.type === 'IV_VOLUME') {
    return {
      ML_250: '250ML',
      ML_500: '500ML',
      ML_1000: '1000ML',
    }[action.typeID];
  }
  if (action.type === 'MEDICATION_ROUTE') {
    const route = routes.find((route) => route.key === action.typeID);
    const routeLabel = route ? route.label.en : action.typeID;
    const adminType = adminTypes.find(
      (adminType) => adminType.key === action.subComponentKey
    );
    const adminTypeLabel = adminType
      ? adminType.label.en
      : action.subComponentKey;
    return (
      action.label +
      ' - ' +
      routeLabel +
      (adminTypeLabel ? ' - ' + adminTypeLabel : '')
    );
  }
  if (action.type === 'INTERVENTION') {
    const intervention = interventions.find(intv => intv.key === action.typeID);
    const location = physicalAssessmentLocations.find(
      location => location.key === action.subComponentKey
    );
    if (intervention && location) {

      return location.label.en + ' - ' + intervention.label.en;
    }
  }
  if (action.type === 'INFUSION_FLUID') {
    return startCase(action.typeID.replace(/_/g, ' ').toLowerCase());
  }
  if (action.typeID === 'NONE') {
    return '';
  }
  return action.label || action.typeID;
}

function categoryLabel(action) {
  if (typeLabels[action.type]) {
    return typeLabels[action.type];
  } else if (action.type) {
    return startCase(action.type.replace(/_/g, ' ').toLowerCase());
  } else {
    return 'UNKNOWN';
  }
}

function categoryVerb(action) {
  if (action.typeID === 'NONE') {
    return 'Stopped';
  } if (typeVerbs[action.type]) {
    return typeVerbs[action.type];
  } else {
    return 'Done';
  }
}

const successfulCriticalActions = (report) => {
  return [
    ...report.successfullyTakenActions,
    ...report.successfullyAvoidedActions,
  ].filter((action) => action.isCritical).length;
};

const totalCriticalActions = (report) => {
  return [
    ...report.successfullyTakenActions,
    ...report.successfullyAvoidedActions,
    ...report.failedToTakeActions,
    ...report.failedToAvoidActions,
  ].filter((action) => action.isCritical).length;
};

const formatReportDate = (report) => {
  return moment(report.attemptDate).format('MMMM D, YYYY h:mm A');
};

function getGradingActionFromTimelineAction(
  gradingReport,
  timelineAction
) {
  const { successfullyTakenActions, failedToAvoidActions } = gradingReport;

  // The action was graded by one or more grading effects
  if (timelineAction.gradingTypeIDs && timelineAction.gradingTypeIDs.length) {
    // Find the corresponding grading actions for those effects
    // and aggregate them
    const gradingActions = timelineAction.gradingTypeIDs.map((gradingTypeId) =>
      getGradingActionFromTimelineAction(gradingReport, {
        type: 'GRADING',
        typeID: gradingTypeId,
      })
    );
    return {
      points: sumBy(gradingActions, 'points'),
      isCritical: gradingActions.some(a => a.isCritical),
      isSuccess: gradingActions.some(a => a.isSuccess) && gradingActions.every(a => !a.isFailure),
      isFailure: gradingActions.some(a => a.isFailure),
    }
  }

  // IV actions are associated with several grading actions
  // In this case we return a special combined grading action
  if (timelineAction.type === 'IV_ACTION') {
    const extraInfos = fromPairs(timelineAction.extraInfos.map(extraInfo => [extraInfo.key, extraInfo.value]));
    const successfulGradingActions = successfullyTakenActions.filter(a => a.type.startsWith('IV_')).filter(a => extraInfos[a.type] === a.typeID);
    const failedGradingActions = failedToAvoidActions.filter(a => a.type.startsWith('IV_')).filter(a => extraInfos[a.type] === a.typeID);
    const allGradingActions = [...successfulGradingActions, ...failedGradingActions];
    return {
      points: null,
      isCritical: allGradingActions.some(a => a.isCritical),
      isSuccess: !!successfulGradingActions.length && !failedGradingActions.length,
      isFailure: !!failedGradingActions.length,
    }
  }

  const findGradingAction = (actionList) =>
    actionList.find((gradingAction) => {
      const extraInfos = fromPairs((timelineAction.extraInfos || []).map(extraInfo => [extraInfo.key, extraInfo.value]));
      if (
        timelineAction.type === 'MEDICATION' &&
        gradingAction.type === 'MEDICATION_ROUTE'
      ) {
        return (
          timelineAction.label === gradingAction.label &&
          extraInfos.MEDICATION_ROUTE === gradingAction.typeID
        );
      }
      if (
        timelineAction.type === 'OXYGEN' &&
        gradingAction.type === 'OXYGEN_TYPE'
      ) {
        return timelineAction.typeID === gradingAction.typeID;
      }
      return (
        timelineAction.type === gradingAction.type &&
        timelineAction.typeID === gradingAction.typeID
      );
    });

  const successfulGradingAction = findGradingAction(successfullyTakenActions);
  const failedGradingAction = findGradingAction(failedToAvoidActions);
  const gradingAction = successfulGradingAction || failedGradingAction;
  if (!gradingAction) {
    return null;
  }
  return {
    ...gradingAction,
    isSuccess: !!successfulGradingAction,
    isFailure: !!failedGradingAction,
  };
}

function extraInfos(timelineAction) {
  if (!timelineAction.extraInfos) {
    return {};
  }
  return fromPairs(timelineAction.extraInfos.map(extraInfo => [extraInfo.key, extraInfo.value]));
}

function extraInfosLabel(timelineAction) {
  if (timelineAction.type === 'MEDICATION') {
    return `${extraInfos(timelineAction).MEDICATION_DOSE }${unitLabel(extraInfos(timelineAction).MEDICATION_UNIT_TYPE)}`;
  }
  if (timelineAction.type === 'IV_ACTION') {
    const { extraInfos } = timelineAction;
    return extraInfos.filter(extraInfo => !(extraInfo.key === 'IV_SALINE_LOCK' && extraInfo.value === 'False')).map(extraInfo => actionLabel({
      type: extraInfo.key,
      typeID: extraInfo.value
    })).join(' ')
  }
  if (timelineAction.type === 'OXYGEN' && timelineAction.typeID !== 'NONE') {
    return `${extraInfos(timelineAction).OXYGEN_DOSE}L`;
  }
  return '';
}

module.exports = {
  actionTypeOrderMap,
  allActions,
  totalPoints,
  successfulPoints,
  percentage,
  typeLabels,
  actionLabel,
  categoryLabel,
  categoryVerb,
  successfulCriticalActions,
  totalCriticalActions,
  formatReportDate,
  getGradingActionFromTimelineAction,
  extraInfosLabel,
};
